import { useState } from "react";

import { Button, InputSelect } from "../../ui";
import useMSAL from "../../../hooks/useMSAL";
import {
  buildExportPayload,
  DEFAULT_INCLUDE_IMAGES,
  DEFAULT_REPORT_FORMAT,
  downloadBlobAsFile,
  fetchReportWithTokenRefresh,
  getDefaultReportForType,
  getReportFormatMeta,
  getReportMeta,
  getSupportedReportsForType,
  parseFilenameFromContentDisposition,
  readErrorMessage,
  REPORT_CATALOGS,
  supportsImages,
  SUPPORTED_REPORT_FORMATS,
} from "./utils";

const GenerateReportBody = props => {
  const {
    closeModal,
    ids = [],
    listId,
    search,
    select,
    type = "Contact",
    // `report` is the *initial* selection — the user can change it via the
    // dropdown before clicking Generate. Defaults to the type's default
    // report so each entry point lands on something sensible.
    report: initialReport,
  } = props || {};

  const catalog = REPORT_CATALOGS[type] || REPORT_CATALOGS.Contact;
  const supportedReports = getSupportedReportsForType(type);
  const defaultReport = initialReport || getDefaultReportForType(type);

  const { requestAccessToken } = useMSAL();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);
  const [format, setFormat] = useState(DEFAULT_REPORT_FORMAT);
  const [report, setReport] = useState(defaultReport);
  const [includeImages, setIncludeImages] = useState(DEFAULT_INCLUDE_IMAGES);

  const formatMeta = getReportFormatMeta(format);
  const reportMeta = getReportMeta(report);
  const defaultFilename = `${reportMeta.defaultFilenameSlug}.${formatMeta.extension}`;
  // Reports without artwork thumbnails (e.g. requests) hide the toggle:
  // there's nothing the flag could change, and showing the slow-with-many-
  // rows warning would be misleading.
  const reportSupportsImages = supportsImages(report);
  // Format options scoped to this report — XLSX only when the catalog
  // declares a renderer for it. Today every Contact report has both
  // PDF + XLSX; the Artfair Price List is PDF-only.
  const reportFormats =
    Array.isArray(reportMeta.formats) && reportMeta.formats.length > 0
      ? reportMeta.formats
      : SUPPORTED_REPORT_FORMATS;

  const reportOptions = supportedReports.map(option => ({
    value: option,
    label: getReportMeta(option).menuLabel,
  }));
  const selectedReportOption = reportOptions.find(
    option => option.value === report,
  );

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setElapsedMs(null);

    const startedAt = performance.now();
    try {
      const payload = buildExportPayload({
        type,
        ids,
        listId,
        search,
        select,
        format,
        report,
        includeImages,
      });
      const accept = formatMeta.accept;

      let token = localStorage.getItem("user:token");
      let response = await fetchReportWithTokenRefresh({
        token,
        payload,
        accept,
        requestAccessToken,
        onSessionExpired: () => window.location.reload(),
      });

      if (!response.ok) {
        const message = await readErrorMessage(response);
        throw new Error(message);
      }

      const blob = await response.blob();
      const filename =
        parseFilenameFromContentDisposition(
          response.headers.get("Content-Disposition"),
        ) || defaultFilename;

      downloadBlobAsFile({ blob, filename });
      setElapsedMs(Math.round(performance.now() - startedAt));
      closeModal?.();
    } catch (err) {
      console.error("[GenerateReport] failed", err);
      setError(err.message || `Failed to generate ${reportMeta.sentenceLabel}`);
    } finally {
      setLoading(false);
    }
  };

  const targetDescription = (() => {
    const noun = catalog.targetNoun;
    const nounPlural = catalog.targetNounPlural;
    if (select === "all" && listId) return `the saved list (id ${listId})`;
    if (select === "all") return `all ${nounPlural} matching the current search`;
    return `${ids.length} selected ${noun}${ids.length === 1 ? "" : "s"}`;
  })();

  return (
    <div className="mt-6 flex w-full flex-col">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Generate Report</div>
          <div className="pt-4 text-base font-normal">
            This will generate a {formatMeta.label} {reportMeta.sentenceLabel}{" "}
            for {targetDescription}. The file will download automatically when
            ready.
          </div>
          <div className="max-w-xs pt-4">
            <InputSelect
              label="Report"
              options={reportOptions}
              selected={selectedReportOption}
              disabled={loading}
              onChange={value => setReport(value)}
            />
          </div>
          <div className="flex items-center gap-3 pt-4 text-sm">
            <span className="font-medium">Format:</span>
            {reportFormats.map(option => {
              const optionMeta = getReportFormatMeta(option);
              return (
                <label
                  key={option}
                  className="flex cursor-pointer items-center gap-1"
                >
                  <input
                    type="radio"
                    name="report-format"
                    value={option}
                    checked={format === option}
                    disabled={loading}
                    onChange={() => setFormat(option)}
                  />
                  <span>{optionMeta.label}</span>
                </label>
              );
            })}
          </div>
          {reportSupportsImages ? (
            <div className="pt-4 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  name="include-images"
                  checked={includeImages}
                  disabled={loading}
                  onChange={event => setIncludeImages(event.target.checked)}
                />
                <span className="font-medium">Include images</span>
              </label>
              {includeImages ? (
                <div className="mt-1 text-xs text-gray-500">
                  NOTE: including images can noticeably slow generation when
                  exporting a large number of {catalog.targetNounPlural}.
                </div>
              ) : null}
            </div>
          ) : null}
          {error ? (
            <div className="pt-4 text-sm text-red-600">{error}</div>
          ) : null}
          {elapsedMs != null ? (
            <div className="pt-4 text-sm text-gray-500">
              Generated in {elapsedMs}ms.
            </div>
          ) : null}
        </div>
        <div className="flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              disabled={loading}
              onClick={() => closeModal?.()}
            />
            <Button
              label={loading ? "Generating…" : "Generate"}
              disabled={loading}
              loading={loading}
              onClick={handleGenerate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { GenerateReportBody };
export default GenerateReportBody;
